<template>
  <div class="h-screen overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div class="overflow-y-auto">
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
        <h3 class="font-semibold mt-8 truncate">
          <span class="text-gray-dark">{{ botDisplayName }}</span>
          <span class="text-gray ml-1">/</span>
          <span class="text-black ml-1">Add channel</span>
        </h3>
      </div>

      <div class="px-8 mt-8">
        <base-label id="newChannelName" label="Name"/>
        <base-input id="newChannelName"
                    name="newChannelName"
                    variant="editor"
                    type="string"
                    class="w-full border-gray py-px mt-1 h-10"
                    v-model.trim="channelName"
        />
      </div>

      <div class="px-8 mt-6">
        <base-label id="newChannelType" label="Type"/>
        <queryable-select id="newChannelType"
                          :value="channelType"
                          :options="channelTypeOptions"
                          class="w-full mt-1 h-10"
                          @input="channelType = $event"
        />
      </div>

      <div class="px-8 mt-8 pt-6 border-0 border-t border-solid border-gray-border text-gray-dark font-semibold text-black">
        CHAT SETTINGS
      </div>

      <SettingsBotsForm :selected-theme="selectedTheme"
                        :selected-lang-config="selectedLangConfig"
                        :selected-language="selectedLanguage"
                        :is-text-override-enabled="isTextOverrideEnabled"
                        :themes="themes"
                        @update:selected-theme="updateSelectedTheme"
                        @update:selected-lang-config="updateSelectedLangConfig"
                        @update:selected-language="updateSelectedLanguage"
                        @update:is-text-override-enabled="updateIsTextOverrideEnabled"
                        class="px-8"
      />
    </div>

    <div class="grid grid-cols-2 gap-4 mb-6 px-8">
      <busy-button id="addChannelSaveButton"
                   variant="primary"
                   class="col-span-1"
                   :is-loading="isLoading"
                   :is-disabled="isSaveDisabled || isLoading"
                   @click="submit"
      >
        Create
      </busy-button>
      <base-button variant="bordered"
                   class="col-span-1 justify-center"
                   @click="$emit('cancel')"
      >
        Cancel
      </base-button>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import BaseButton from '@/components/base-components/BaseButton.vue'
import BaseInput from '@/components/base-components/BaseInput.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import SettingsBotsForm from '@/components/settings/bots/SettingsBotsForm.vue'
import chatThemeService from '@/services/amio/chat-theme.service'
import equals from 'ramda/src/equals'
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import type from 'ramda/src/type'
import filter from 'ramda/src/filter'
import map from 'ramda/src/map'
import pipe from 'ramda/src/pipe'
import reject from 'ramda/src/reject'
import anyPass from 'ramda/src/anyPass'

export default {
  name: 'SettingsBotAddChannelEditor',

  components: {
    BusyButton,
    BaseButton,
    BaseInput,
    QueryableSelect,
    SettingsBotsForm
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['cancel', 'create'],

  data() {
    return {
      channelName: '',
      channelType: 'amio_chat',
      themes: [],
      selectedTheme: {
        themeConfig: {},
        langConfig: {}
      },
      originalTheme: {},
      isTextOverrideEnabled: false
    }
  },

  computed: {
    botDisplayName() {
      return this.bot.multi_bot_name || this.bot.multiBotName || this.bot.name
    },
    channelTypeOptions() {
      return [{ text: 'Amio Chat', value: 'amio_chat' }]
    },
    selectedLangConfig() {
      return this.selectedTheme.langConfig?.default || { language: this.bot.language || 'en' }
    },
    selectedLanguage() {
      return this.selectedTheme.langConfig?.default?.language || this.bot.language || 'en'
    },
    isSaveDisabled() {
      return !this.channelName || !this.channelType
    }
  },

  methods: {
    cleanObject(obj) {
      if (isNil(obj) || type(obj) !== 'Object') return obj

      const isFalsy = anyPass([isNil, equals(''), isEmpty])
      const cleanedEntries = pipe(
        Object.entries,
        reject(([key, value]) => isFalsy(value)),
        map(([key, value]) => {
          if (type(value) === 'Object') {
            const cleanedValue = this.cleanObject(value)
            return isEmpty(cleanedValue) ? null : [key, cleanedValue]
          }
          return [key, value]
        }),
        filter(entry => entry !== null)
      )(obj)

      return Object.fromEntries(cleanedEntries)
    },
    updateIsTextOverrideEnabled(isTextOverrideEnabled) {
      this.isTextOverrideEnabled = isTextOverrideEnabled
    },
    updateSelectedLangConfig(langConfig) {
      this.selectedTheme = {
        ...this.selectedTheme,
        langConfig: {
          ...this.selectedTheme.langConfig,
          default: langConfig ? this.cleanObject(langConfig) : {}
        }
      }
    },
    updateSelectedLanguage(language) {
      this.selectedTheme = {
        ...this.selectedTheme,
        langConfig: {
          ...this.selectedTheme.langConfig,
          default: {
            ...(this.selectedTheme.langConfig?.default || {}),
            language
          }
        }
      }
    },
    updateSelectedTheme(theme) {
      const cleanThemeConfig = theme?.themeConfig ? this.cleanObject(theme.themeConfig) : {}
      const cleanLangConfig = theme?.langConfig ? this.cleanObject(theme.langConfig) : {}
      this.selectedTheme = {
        ...(theme || {}),
        themeConfig: cleanThemeConfig,
        langConfig: cleanLangConfig
      }
    },
    submit() {
      this.$emit('create', {
        name: this.channelName,
        type: this.channelType,
        selectedTheme: this.selectedTheme,
        originalTheme: this.originalTheme,
        selectedLanguage: this.selectedLanguage,
        isTextOverrideEnabled: this.isTextOverrideEnabled
      })
    },
    async initThemes() {
      this.themes = await chatThemeService.list()
      if (!this.bot.chat_theme_id) {
        this.selectedTheme = this.themes[0] || { themeConfig: {}, langConfig: {} }
      } else {
        this.selectedTheme = await chatThemeService.get(this.bot.chat_theme_id)
      }

      this.selectedTheme = {
        ...this.selectedTheme,
        langConfig: {
          ...(this.selectedTheme.langConfig || {}),
          default: {
            ...(this.selectedTheme.langConfig?.default || {}),
            language: this.selectedLanguage
          }
        }
      }
      this.originalTheme = JSON.parse(JSON.stringify(this.selectedTheme))
    }
  },

  async mounted() {
    await this.initThemes()
  }
}
</script>
